import { useEffect, useState } from "react";
import { Check, ArrowLeft, ClipboardCheck } from "lucide-react";
import { Card } from "../components/UI";
import { PageLoader } from "../components/LoadingSpinner";
import { InlineError } from "../components/ErrorAlert";
import systemEvaluationApi from "../api/systemEvaluation";

const RESPONDENT_TYPES = [
  { value: "obstetrician_gynecologist", label: "Obstetrician and Gynecologist" },
  { value: "doctor", label: "Dokter" },
  { value: "midwife", label: "Bidan" },
  { value: "trainer", label: "Trainer Khusus" },
  { value: "it_expert", label: "Pakar IT" },
];

const AGE_GROUPS = [
  { value: "20-29", label: "20-29 tahun" },
  { value: "30+", label: "≥ 30 tahun" },
];

const USAGE_REASONS = [
  { value: "feasibility_test", label: "Tes Kelayakan" },
  { value: "mental_health_detection", label: "Deteksi kesehatan mental" },
  { value: "health_information", label: "Mendapat informasi tentang kesehatan" },
  { value: "curiosity", label: "Rasa Ingin Tahu" },
  { value: "other", label: "Alasan Lain" },
];

const QUALITATIVE_FIELDS = [
  {
    key: "qualitative_before",
    label:
      "Pandangan Bapak/Ibu terhadap kasus post partum depresi SEBELUM ada Stress Watch dan Aplikasi Bunda Pintar",
  },
  {
    key: "qualitative_after",
    label:
      "Perubahan pandangan terhadap kasus post partum depresi SETELAH ada Stress Watch dan Aplikasi Bunda Pintar",
  },
  {
    key: "qualitative_response",
    label: "Respons Bapak/Ibu terhadap Stress Watch dan Aplikasi Bunda Pintar",
  },
  {
    key: "qualitative_benefit",
    label: "Persepsi manfaat Stress Watch dan Aplikasi Bunda Pintar",
  },
  { key: "qualitative_suggestion", label: "Saran untuk perbaikan" },
];

const SCALE = [1, 2, 3, 4, 5];

function LikertSection({ title, items, scores, onChange }) {
  return (
    <Card className="p-5 mb-5">
      <h3 className="font-display font-700 text-gray-900 mb-1">{title}</h3>
      <p className="text-xs text-gray-400 mb-4">
        Skala 1 (Sangat Tidak Setuju) — 5 (Sangat Setuju)
      </p>
      <div className="space-y-4">
        {items.map((text, i) => (
          <div key={i} className="border-b border-gray-50 pb-4 last:border-0 last:pb-0">
            <p className="text-sm text-gray-800 mb-2">
              <span className="text-primary-700 mr-1.5">{i + 1}.</span>
              {text}
            </p>
            <div className="flex gap-2">
              {SCALE.map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => onChange(i, n)}
                  className={`w-9 h-9 rounded-lg border text-sm font-semibold transition-all
                    ${
                      scores[i] === n
                        ? "bg-primary-800 border-primary-800 text-white"
                        : "border-gray-200 text-gray-500 hover:border-gray-300"
                    }`}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

export default function SystemEvaluationForm({ onBack }) {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const [respondentName, setRespondentName] = useState("");
  const [respondentType, setRespondentType] = useState("");
  const [ageGroup, setAgeGroup] = useState("");
  const [education, setEducation] = useState("");
  const [usageReason, setUsageReason] = useState("");
  const [usageReasonOther, setUsageReasonOther] = useState("");

  const [susScores, setSusScores] = useState([]);
  const [satisfactionScores, setSatisfactionScores] = useState([]);
  const [designScores, setDesignScores] = useState([]);
  const [qual, setQual] = useState({});

  useEffect(() => {
    systemEvaluationApi
      .getItems()
      .then((res) => {
        const data = res.data?.data;
        setItems(data);
        setSusScores(new Array(data.sus_items.length).fill(0));
        setSatisfactionScores(new Array(data.satisfaction_items.length).fill(0));
        setDesignScores(new Array(data.design_items.length).fill(0));
      })
      .catch(() => setError("Gagal memuat daftar pernyataan kuesioner"))
      .finally(() => setLoading(false));
  }, []);

  const setScore = (setter) => (index, value) =>
    setter((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });

  const validate = () => {
    if (!respondentType) return "Pilih jenis responden terlebih dahulu";
    if (!ageGroup) return "Pilih kelompok usia terlebih dahulu";
    if (!usageReason) return "Pilih alasan penggunaan terlebih dahulu";
    if (usageReason === "other" && !usageReasonOther.trim())
      return "Isi alasan lain terlebih dahulu";
    if (susScores.some((s) => !s)) return "Lengkapi semua pernyataan SUS";
    if (satisfactionScores.some((s) => !s))
      return "Lengkapi semua pernyataan Kegunaan & Kepuasan";
    if (designScores.some((s) => !s))
      return "Lengkapi semua pernyataan Desain Format Tampilan";
    return "";
  };

  const submit = async () => {
    const v = validate();
    if (v) {
      setError(v);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    setError("");
    setSaving(true);
    try {
      const res = await systemEvaluationApi.submit({
        respondent_name: respondentName.trim() || undefined,
        respondent_type: respondentType,
        age_group: ageGroup,
        education: education.trim() || undefined,
        usage_reason: usageReason,
        usage_reason_other:
          usageReason === "other" ? usageReasonOther.trim() : undefined,
        sus_scores: susScores,
        satisfaction_scores: satisfactionScores,
        design_scores: designScores,
        ...qual,
      });
      setResult(res.data?.data ?? null);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setError(
        err.response?.data?.message || "Gagal mengirim kuesioner, coba lagi",
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <PageLoader message="Memuat kuesioner..." />;

  if (result) {
    return (
      <div className="max-w-2xl mx-auto p-6 py-16 text-center">
        <div className="w-20 h-20 rounded-full bg-green-50 border-2 border-green-200 flex items-center justify-center mx-auto mb-4">
          <Check className="w-10 h-10 text-green-600" />
        </div>
        <h2 className="font-display font-800 text-2xl text-gray-900 mb-1">
          Terima Kasih!
        </h2>
        <p className="text-sm text-gray-500 mb-6">
          Kuesioner Kepuasan Sistem berhasil dikirim.
        </p>
        <Card className="p-6 text-left max-w-md mx-auto">
          <div className="grid grid-cols-3 gap-3 text-center mb-4">
            <div>
              <p className="text-xs text-gray-400">SUS</p>
              <p className="font-display font-800 text-lg text-gray-900">
                {result.sus_average}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-400">Kepuasan</p>
              <p className="font-display font-800 text-lg text-gray-900">
                {result.satisfaction_average}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-400">Desain</p>
              <p className="font-display font-800 text-lg text-gray-900">
                {result.design_average}
              </p>
            </div>
          </div>
          <div className="text-center pt-4 border-t border-gray-100">
            <p className="text-xs text-gray-400 mb-1">Rata-rata Keseluruhan</p>
            <p className="font-display font-900 text-3xl text-primary-800">
              {result.overall_average}
            </p>
            <span className="inline-block mt-2 text-xs font-bold px-3 py-1 rounded-full bg-primary-50 text-primary-800 border border-primary-100">
              {result.feasibility_category}
            </span>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-6">
      {onBack && (
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-gray-500 hover:text-gray-700 text-sm mb-5"
        >
          <ArrowLeft className="w-4 h-4" /> Kembali
        </button>
      )}

      <div className="flex items-center gap-3 mb-6">
        <div className="w-11 h-11 rounded-xl bg-primary-800 flex items-center justify-center flex-shrink-0">
          <ClipboardCheck className="w-5 h-5 text-white" />
        </div>
        <div>
          <h2 className="font-display font-800 text-xl text-gray-900">
            Kuesioner Kepuasan Sistem
          </h2>
          <p className="text-sm text-gray-500">
            Stress Watch &amp; Aplikasi Bunda Pintar (SmartMoms) — untuk
            validator ahli
          </p>
        </div>
      </div>

      <InlineError message={error} />

      {/* Data Responden */}
      <Card className="p-5 mb-5">
        <h3 className="font-display font-700 text-gray-900 mb-4">
          Data Responden
        </h3>

        <div className="space-y-4">
          <div>
            <label className="text-xs font-semibold text-gray-500 mb-1.5 block">
              Nama (opsional)
            </label>
            <input
              value={respondentName}
              onChange={(e) => setRespondentName(e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-500 mb-1.5 block">
              Responden
            </label>
            <div className="grid grid-cols-2 gap-2">
              {RESPONDENT_TYPES.map((r) => (
                <button
                  key={r.value}
                  type="button"
                  onClick={() => setRespondentType(r.value)}
                  className={`px-3 py-2.5 rounded-xl border text-xs font-medium text-left transition-all
                    ${
                      respondentType === r.value
                        ? "border-primary-500 bg-primary-50 text-primary-800"
                        : "border-gray-200 text-gray-600 hover:border-gray-300"
                    }`}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-500 mb-1.5 block">
              Umur
            </label>
            <div className="flex gap-2">
              {AGE_GROUPS.map((a) => (
                <button
                  key={a.value}
                  type="button"
                  onClick={() => setAgeGroup(a.value)}
                  className={`px-4 py-2 rounded-xl border text-sm font-medium transition-all
                    ${
                      ageGroup === a.value
                        ? "border-primary-500 bg-primary-50 text-primary-800"
                        : "border-gray-200 text-gray-600 hover:border-gray-300"
                    }`}
                >
                  {a.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-500 mb-1.5 block">
              Pendidikan (opsional)
            </label>
            <input
              value={education}
              onChange={(e) => setEducation(e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-500 mb-1.5 block">
              Alasan menggunakan Stress Watch dan Aplikasi Bunda Pintar
            </label>
            <div className="grid grid-cols-2 gap-2 mb-2">
              {USAGE_REASONS.map((u) => (
                <button
                  key={u.value}
                  type="button"
                  onClick={() => setUsageReason(u.value)}
                  className={`px-3 py-2.5 rounded-xl border text-xs font-medium text-left transition-all
                    ${
                      usageReason === u.value
                        ? "border-primary-500 bg-primary-50 text-primary-800"
                        : "border-gray-200 text-gray-600 hover:border-gray-300"
                    }`}
                >
                  {u.label}
                </button>
              ))}
            </div>
            {usageReason === "other" && (
              <input
                value={usageReasonOther}
                onChange={(e) => setUsageReasonOther(e.target.value)}
                placeholder="Sebutkan alasan lain..."
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            )}
          </div>
        </div>
      </Card>

      {/* SUS */}
      <LikertSection
        title="Kegunaan (System Usability Scale)"
        items={items.sus_items}
        scores={susScores}
        onChange={setScore(setSusScores)}
      />

      {/* Satisfaction */}
      <LikertSection
        title="Kegunaan dan Kepuasan terhadap Stress Watch dan Aplikasi Bunda Pintar"
        items={items.satisfaction_items}
        scores={satisfactionScores}
        onChange={setScore(setSatisfactionScores)}
      />

      {/* Design */}
      <LikertSection
        title="Kuesioner Desain Format Tampilan Aplikasi Smartmoms dan Smart Watch"
        items={items.design_items}
        scores={designScores}
        onChange={setScore(setDesignScores)}
      />

      {/* Qualitative */}
      <Card className="p-5 mb-5">
        <h3 className="font-display font-700 text-gray-900 mb-4">
          Pertanyaan Kualitatif
        </h3>
        <div className="space-y-4">
          {QUALITATIVE_FIELDS.map((f) => (
            <div key={f.key}>
              <label className="text-xs font-semibold text-gray-500 mb-1.5 block">
                {f.label}
              </label>
              <textarea
                rows={3}
                value={qual[f.key] || ""}
                onChange={(e) =>
                  setQual((prev) => ({ ...prev, [f.key]: e.target.value }))
                }
                className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
              />
            </div>
          ))}
        </div>
      </Card>

      <button
        onClick={submit}
        disabled={saving}
        className="w-full py-3 bg-primary-800 hover:bg-primary-900 disabled:opacity-50 text-white font-semibold rounded-xl transition-colors mb-10"
      >
        {saving ? "Mengirim..." : "Kirim Kuesioner"}
      </button>
    </div>
  );
}
